/* A SOCIAL ARC — a series of posts where each one builds on the one before.
 *
 * Her decision, 2026-09-25 (docs/social-redesign-spec.md, phase B): a WIZARD.
 * She sets it up (what it's about, 3 / 7 / 14 days, how many posts, platform,
 * the offer it builds to, the last post's ask), Brill plans it ONCE, then the
 * posts are written one at a time, each drafted with her CORRECTED earlier posts
 * in front of Brill — so the series follows on from what she actually kept, not
 * from the plan's wording.
 *
 * WHERE THINGS LIVE
 *   social_arcs      one row per arc. `brief` (0034) holds what she set up and
 *                    Brill's plan: { about, days, count, ask, length (an id,
 *                    or "mixed"), goal,
 *                    library: [{ id, kind, label }], pictures: [{ id, url,
 *                    about }], slots: [{ n, date, topic, pillar, theme, hook,
 *                    ask, length (mixed only), picks: [libraryId], picture:
 *                    poolId }] }.
 *                    `offer_id` (0034) is the offer it builds to. `platform`,
 *                    `start_date`, `end_date`, `theme_mix` are 0001/0002's.
 *   social_posts     an arc's posts are ordinary posts with `arc_id` set and
 *                    their place in `inputs.arcSlot` (1-based). A slot is
 *                    WRITTEN when a post in this arc holds it and has words —
 *                    counted from the posts, never stored on the arc, so the two
 *                    can never disagree (rule 11: done is counted, not claimed).
 *
 * THE PLAN IS READ THROUGH A DELIMITED BLOCK (`parseArcPlan`), because reading
 * Brill's prose is the seam in this app that fails silently. Anything the block
 * gets wrong is either repaired to a safe default (an unknown pillar → Brill
 * picks later) or refused out loud (too few posts) — never quietly half-used.
 */
import { createRecordStore } from "./recordStore";
import { aboutFromLibrary, kindOf, needsPermission } from "./library";
import { arcBasisOf, pickedPicture } from "./socialDraft";
import { withPicture } from "./socialPosts";

export const SOCIAL_ARC_FIELDS = ["name", "platform", "startDate", "endDate", "status", "themeMix", "offerId", "brief"];

export const useSocialArcs = createRecordStore({
  table: "social_arcs",
  fields: SOCIAL_ARC_FIELDS,
  idPrefix: "arc",
});

/* Days, not posts (her call): 3 / 7 / 14 days, a post every other day by
   default. The count is hers to change, never more posts than days. */
export const ARC_LENGTHS = [3, 7, 14];
export const defaultPostCount = (days) => Math.max(1, Math.ceil(days / 2));
export const clampPostCount = (count, days) => Math.min(Math.max(1, Math.round(count) || 1), days);

/* Local calendar dates as YYYY-MM-DD, never through toISOString — that is UTC,
   and in her evening it is already tomorrow. */
const pad = (n) => String(n).padStart(2, "0");
export const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
export const addDays = (iso, n) => {
  const d = fromIso(iso);
  if (!d) return null;
  d.setDate(d.getDate() + n);
  return isoDay(d);
};
export const tomorrow = (now = new Date()) => { const d = new Date(now); d.setDate(d.getDate() + 1); return isoDay(d); };

/* SPREAD ACROSS THE DAYS, first and last day both used: 7 days and 4 posts is
   day 0, 2, 4, 6 — Mon, Wed, Fri, Sun, as she drew it. */
export function slotDates(startIso, days, count) {
  const n = clampPostCount(count, days);
  if (n === 1) return [startIso];
  const step = (days - 1) / (n - 1);
  return Array.from({ length: n }, (_, i) => addDays(startIso, Math.round(i * step)));
}

export const dayLabel = (iso) => {
  const d = fromIso(iso);
  return d ? d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "";
};

/* ── The plan ─────────────────────────────────────────────────────────────── */
export const ARC_PLAN_START = "<<<ARC_PLAN>>>";
export const ARC_PLAN_END = "<<<ARC_END>>>";

/**
 * Brill's plan, read. Returns { name, goal, slots } or { error } — never a
 * half-read plan presented as a whole one.
 *
 * @param count     how many posts she asked for — fewer is refused, more trimmed
 * @param pillars   her pillars ({ label, themes }) — an unknown one becomes ""
 * @param hookIds   the hook styles — an unknown one becomes ""
 * @param finalAsk  HER ask, for the last post. Brill only ever says "final":
 *                  the last post always carries hers, and the posts before it
 *                  may only be "none" or "message" (never a sale mid-series).
 * @param dates     the slot dates, in order
 * @param library   `planLibrary` — what she picked from her Library. EVERY one
 *                  ends up in exactly one post: a key Brill invents or repeats
 *                  is dropped, and one it leaves out is placed for it.
 * @param pictures  `planPictures` — the arc's picture pool, same rule. A post
 *                  whose Library pick brings a picture never takes one.
 * @param mixed     she chose MIXED length: each post gets Brill's "length",
 *                  and a plan with one length for every post is varied here.
 */
export function parseArcPlan(text, { count, pillars = [], hookIds = [], finalAsk = "none", dates = [], library = [], pictures = [], mixed = false } = {}) {
  const raw = String(text || "");
  const a = raw.indexOf(ARC_PLAN_START);
  const b = a >= 0 ? raw.indexOf(ARC_PLAN_END, a) : -1;
  if (a < 0 || b < 0) return { error: "Brill didn't send a plan back. Try again." };
  let json;
  try {
    json = JSON.parse(raw.slice(a + ARC_PLAN_START.length, b).replace(/```json|```/g, "").trim());
  } catch {
    return { error: "Brill's plan came back garbled. Try again." };
  }
  const posts = Array.isArray(json?.posts) ? json.posts : [];
  const usable = posts.filter((p) => p && String(p.topic || "").trim());
  if (usable.length < count) {
    return { error: `Brill planned ${usable.length} post${usable.length === 1 ? "" : "s"} instead of ${count}. Try again.` };
  }
  const slots = usable.slice(0, count).map((p, i) => {
    const pillar = pillars.find((x) => x.label === String(p.pillar || "").trim()) || null;
    const theme = pillar && (pillar.themes || []).includes(String(p.theme || "").trim()) ? String(p.theme).trim() : "";
    const hook = hookIds.includes(String(p.hook || "").trim()) ? String(p.hook).trim() : "";
    const last = i === count - 1;
    const ask = last ? finalAsk : (String(p.ask || "").trim() === "message" ? "message" : "none");
    const length = mixed ? (LENGTH_IDS.includes(String(p.length || "").trim()) ? String(p.length).trim() : "medium") : "";
    return { n: i + 1, date: dates[i] || null, topic: String(p.topic).trim(), pillar: pillar ? pillar.label : "", theme, hook, ask, length, picks: [], picture: "" };
  });
  placePicks(slots, usable, library);
  placePictures(slots, usable, library, pictures);
  if (mixed) varyLengths(slots, library);
  return {
    name: String(json.name || "").trim().slice(0, 80),
    goal: String(json.goal || "").trim().slice(0, 300),
    slots,
  };
}

/* ── Length: one for the arc, or MIXED (her ask, 2026-09-30) ─────────────────
   Mixed means the plan gives each post its own Short / Medium / Long, by what
   that post has to do. The ids are POST_LENGTHS' (socialPosts.js); "mixed" is
   only ever an arc's, never a post's — every post still gets a real length. */
export const MIXED = "mixed";
const LENGTH_IDS = ["short", "medium", "long"];

/* She asked for mixed; a plan with one length throughout is not that, and
   would look fine on every row. Varied here, by what each post carries: a
   post built on a story runs long, and the opener is short — the rest keep
   Brill's. Only when Brill gave them all the same. */
function varyLengths(slots, library) {
  if (slots.length < 2 || new Set(slots.map((s) => s.length)).size > 1) return;
  const told = (s) => s.picks.some((id) => library.find((x) => x.id === id)?.kind === "story");
  slots.forEach((s) => { if (told(s)) s.length = "long"; });
  if (!told(slots[0])) slots[0].length = "short";
  if (new Set(slots.map((s) => s.length)).size === 1) slots[0].length = slots[0].length === "short" ? "long" : "short";
}

/* ── The walkthrough (her call, 2026-09-30: after the plan, one post per
   step). Each change is to the saved plan, so it costs nothing. These two are
   the only moves that touch more than one post, and they keep the plan's
   rules: a Library pick is in ONE post, a story at most one per post, a pool
   picture on one post. Written posts are the screen's to protect — it never
   offers what they hold. */

/* Tap a pick onto post n, or off it. On: it leaves whatever post had it, and
   a story here already is set aside (not used) — never two stories in a post.
   Off: it is set aside. `kindOf(id)` names a pick's kind. */
export function togglePickOnSlot(slots = [], n, pickId, kindOfId = () => null) {
  const here = slots.find((s) => s.n === n);
  const on = (here?.picks || []).includes(pickId);
  const isStory = kindOfId(pickId) === "story";
  return slots.map((s) => {
    let picks = (s.picks || []).filter((id) => id !== pickId);
    if (s.n === n && !on) {
      if (isStory) picks = picks.filter((id) => kindOfId(id) !== "story");
      picks = [...picks, pickId];
    }
    return { ...s, picks };
  });
}

/* Give post n a pool picture ("" for none); it leaves the post that had it. */
export function setPictureOnSlot(slots = [], n, picId) {
  return slots.map((s) => (s.n === n ? { ...s, picture: picId || "" } : picId && s.picture === picId ? { ...s, picture: "" } : s));
}

/* ── Sending the arc to GHL (2026-09-30) ─────────────────────────────────────
   Every WRITTEN post goes as a DRAFT on its planned day, at one time she picks
   for the arc. Worked out here, in her browser, because the day is hers: "Thu
   at 9" means 9 where she is, which the server cannot know. What will happen
   to each post is decided BEFORE anything is sent, so the pop-up can say it.
     send   — goes, dated
     undated — goes, but its day has passed, so it goes without one
     skip   — not written yet, nothing to send */
/* HER CALL, 2026-09-30, after seeing GHL's planner: a draft with no date is
   filed on the moment it arrives, so an arc sent late stacked up on today and
   she had to move every post by hand. So the SEND picks its start too — the
   arc's own while it is still ahead, else today — and every post re-spreads
   from it with the arc's spacing. The arc takes the new days as well, so Brill
   and GHL never disagree about when a post is for. */
export const today = (now = new Date()) => isoDay(now);
export const sendStart = (arc, now = new Date()) => {
  const t = today(now);
  const s = String(arc?.startDate || "").slice(0, 10);
  return s && s >= t ? s : t;
};
export function restartArc(arc, start) {
  const slots = arc?.brief?.slots || [];
  const days = Math.max(slots.length || 1, Number(arc?.brief?.days) || slots.length || 1);
  const dates = slotDates(start, days, slots.length || 1);
  return { ...arc, startDate: start, endDate: addDays(start, days - 1), brief: { ...arc.brief, slots: slots.map((s, i) => ({ ...s, date: dates[i] || s.date })) } };
}

export function arcSendRows(arc, posts = [], { time = "09:00", now = new Date(), start = null } = {}) {
  const [hh, mm] = /^(\d{1,2}):(\d{2})$/.test(time) ? time.split(":").map(Number) : [9, 0];
  const dated = start ? restartArc(arc, start) : arc;
  return arcRows(dated, posts).rows.map(({ slot, post, written }) => {
    const d = fromIso(slot.date);
    const at = d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh, mm) : null;
    if (!written) return { n: slot.n, date: slot.date, post, action: "skip", at: null };
    if (!at || at.getTime() < now.getTime() + 5 * 60000) return { n: slot.n, date: slot.date, post, action: "undated", at: null };
    return { n: slot.n, date: slot.date, post, action: "send", at: at.toISOString() };
  });
}

/* A post's length: its own from a mixed plan, else the arc's. */
export const slotLength = (arc, slot) => (LENGTH_IDS.includes(slot?.length) ? slot.length : (LENGTH_IDS.includes(arc?.brief?.length) ? arc.brief.length : ""));

const asKeys = (v) => (Array.isArray(v) ? v : v ? [v] : []).map((k) => String(k || "").trim());

/* HER LIBRARY PICKS, ONE POST EACH. Brill says which post uses which by key.
   A pick it never placed is NOT dropped — that is the failure she would never
   see (she ticked a story and no post is about it) — it goes to the emptiest
   post, earliest first, and the row names it like any other. One story per
   post: the first live arc (2026-09-28) doubled up when given the room. */
function placePicks(slots, usable, library) {
  if (!library.length) return;
  const kindById = (id) => library.find((x) => x.id === id)?.kind;
  const hasStory = (s) => s.picks.some((id) => kindById(id) === "story");
  const placed = new Set();
  slots.forEach((s, i) => {
    asKeys(usable[i].uses).forEach((k) => {
      const it = library.find((x) => x.key === k);
      if (!it || placed.has(it.key) || (it.kind === "story" && hasStory(s))) return;
      placed.add(it.key);
      s.picks.push(it.id);
    });
  });
  library.filter((it) => !placed.has(it.key)).forEach((it) => {
    const room = slots.filter((s) => it.kind !== "story" || !hasStory(s));
    const home = [...(room.length ? room : slots)].sort((a, b) => (a.picks.length - b.picks.length) || (a.n - b.n))[0];
    home.picks.push(it.id);
  });
}

/* THE POOL'S PICTURES, ONE POST EACH, and only a post with no picture coming
   from its Library pick. Brill matches by her "what's in it" line; a picture
   it left out (or one she never described, which it was not shown) takes the
   next post without one, so nothing she added is silently unused. */
function placePictures(slots, usable, library, pictures) {
  if (!pictures.length) return;
  const bringsOwn = (s) => s.picks.some((id) => library.find((x) => x.id === id)?.hasPicture);
  const placed = new Set();
  slots.forEach((s, i) => {
    const pic = pictures.find((x) => x.key === String(usable[i].picture || "").trim());
    if (!pic || placed.has(pic.key) || bringsOwn(s)) return;
    placed.add(pic.key);
    s.picture = pic.id;
  });
  pictures.filter((p) => !placed.has(p.key)).forEach((p) => {
    const home = slots.find((s) => !s.picture && !bringsOwn(s));
    if (home) home.picture = p.id;
  });
}

/* ── Her Library in an arc (decided 2026-09-29) ──────────────────────────────
   STORIES UP TO THE NUMBER OF POSTS — one per post, so the cap grows with the
   arc (2 / 4 / 7) — and ONE EACH of an idea, an objection and a testimonial:
   ticking a second of those swaps the first. A cap rather than "let Brill sort
   it out", because the first live arc spent a later post's story early when it
   had more than it had posts for. */
export const storyCount = (picks = []) => picks.filter((p) => p.kind === "story").length;

export function togglePick(picks = [], item, count) {
  if (picks.some((p) => p.id === item.id)) return picks.filter((p) => p.id !== item.id);
  if (needsPermission(item)) return picks;
  if (item.kind === "story") return storyCount(picks) >= count ? picks : [...picks, item];
  return [...picks.filter((p) => p.kind !== item.kind), item];
}

/* Fewer posts than stories (she stepped the count down after ticking): the
   first ones she ticked stay. */
export function capPicks(picks = [], count) {
  let stories = 0;
  return picks.filter((p) => p.kind !== "story" || ++stories <= count);
}

/* What the arc keeps about a pick: enough to name it on a row even after she
   deletes it from the Library. Its WORDS are read live when a post is drafted,
   so a story she has since corrected goes to Brill corrected. */
export const pickLabel = (item) => clip(item?.title || item?.who || item?.text, 60);
export const briefPick = (item) => ({ id: item.id, kind: item.kind, label: pickLabel(item) });

/* The picks as the PLAN CALL sees them, keyed L1, L2… — a key, not her row's
   id, so nothing Brill can mistype reaches the database. One she has deleted,
   or a testimonial whose permission she has since unticked, is left out. */
export function planLibrary(arc, library = []) {
  return (arc?.brief?.library || [])
    .map((p) => library.find((i) => i.id === p.id))
    .filter((it) => it && !needsPermission(it))
    .map((it, i) => ({ key: `L${i + 1}`, id: it.id, kind: it.kind, text: clip(aboutFromLibrary(it), 900), hasPicture: !!pickedPicture(it) }));
}

const isLink = (u) => /^https?:\/\/\S+$/i.test(String(u || "").trim());

/* The pool, keyed P1, P2… Brill is only SHOWN the ones she described (it reads
   her line, never the image); the rest are still placed by `parseArcPlan`. */
export function planPictures(arc) {
  return (arc?.brief?.pictures || []).filter((p) => p && isLink(p.url))
    .map((p, i) => ({ key: `P${i + 1}`, id: p.id, about: clip(p.about, 300) }));
}

/* A post's Library items, live. */
export function slotItems(slot, library = []) {
  return (slot?.picks || []).map((id) => library.find((i) => i.id === id)).filter((it) => it && !needsPermission(it));
}

/* The same, for the row: every pick NAMED, including one that can no longer be
   used, so a story that will not reach Brill says so instead of vanishing. */
export function slotPickRows(arc, slot, library = []) {
  return (slot?.picks || []).map((id) => {
    const item = library.find((i) => i.id === id) || null;
    const kept = (arc?.brief?.library || []).find((p) => p.id === id) || null;
    const kind = item?.kind || kept?.kind || "idea";
    return {
      id, kind, kindLabel: kindOf(kind).label,
      label: item ? pickLabel(item) : kept?.label || "",
      gone: !item ? "no longer in your Library" : needsPermission(item) ? "no permission yet" : null,
    };
  });
}

/* ONE ANSWER TO "WHICH PICTURE DOES THIS POST START WITH". The pool picture
   the plan (or she, on the row) gave it, else the picture its story or idea
   brings — never a testimonial's headshot (`pickedPicture`). Only ever a
   STARTING picture: once the post exists, `post.visual` is the answer and the
   post box is where she changes it. */
export function slotPicture(arc, slot, library = []) {
  const pool = (arc?.brief?.pictures || []).find((p) => p.id && p.id === slot?.picture);
  if (pool && isLink(pool.url)) return { url: pool.url.trim(), about: String(pool.about || "").trim(), from: "pool" };
  for (const it of slotItems(slot, library)) {
    const url = pickedPicture(it);
    if (url) return { url, about: "", from: it.kind };
  }
  return null;
}

/* What a post is about, as Brill is told: the plan's topic, then the Library
   items placed in it, each said as what it is (`aboutFromLibrary`). */
export function slotAngle(slot, library = []) {
  const items = slotItems(slot, library).map((it) => clip(aboutFromLibrary(it), 2500));
  return [String(slot?.topic || "").trim(), ...items].filter(Boolean).join("\n\n");
}

/* ── Where an arc stands ─────────────────────────────────────────────────── */
const hasWords = (p) => !!String(p?.caption || "").trim();

/* The post holding a slot. Two posts on one slot (a double press, a second
   browser) is possible; the one with words wins, then the newest. */
export function postForSlot(arcId, n, posts = []) {
  const mine = posts.filter((p) => p && p.arcId === arcId && Number(p.inputs?.arcSlot) === n);
  if (!mine.length) return null;
  return mine.sort((x, y) => (hasWords(y) - hasWords(x)) || String(y.updatedAt || "").localeCompare(String(x.updatedAt || "")))[0];
}

/**
 * Every slot, with its post (or null) and where it stands.
 * `next` is the FIRST unwritten slot — the only one Brill may draft, because
 * each post is written with the ones before it in front of Brill.
 */
export function arcRows(arc, posts = []) {
  const slots = arc?.brief?.slots || [];
  const rows = slots.map((s) => {
    const post = postForSlot(arc.id, s.n, posts);
    return { slot: s, post, written: hasWords(post) };
  });
  const next = rows.find((r) => !r.written) || null;
  return { rows, written: rows.filter((r) => r.written).length, total: rows.length, next };
}

/* One dot per post for the landing table (StateDots): done / draft / empty. */
export function arcStates(arc, posts = []) {
  return arcRows(arc, posts).rows.map((r) => (!r.written ? "empty" : r.post.status === "done" ? "done" : "draft"));
}

const clip = (s, n) => { const t = String(s || "").trim(); return t.length > n ? `${t.slice(0, n).trimEnd()}…` : t; };

/**
 * WHAT BRILL IS TOLD ABOUT THE SERIES when it drafts post `n`: the arc, the
 * posts before it AS SHE FINISHED THEM (their caption now, never the first
 * draft), and what is still to come so it does not spend their material.
 * Bounded: each earlier post is clipped, and only the plan's topics travel
 * for the later ones.
 */
export function arcContext(arc, posts = [], n) {
  if (!arc) return null;
  const { rows } = arcRows(arc, posts);
  const earlier = rows.filter((r) => r.slot.n < n && r.written).map((r) => ({ n: r.slot.n, text: clip(r.post.caption, 2500) }));
  /* A later post's Library pick is RESERVED with its topic. Her stories reach
     every draft (the server's HER REAL STORIES), so without the name an early
     post is free to spend the story a later one was planned around. From the
     label the arc kept — no Library read needed on the post screen's path. */
  const kept = arc.brief?.library || [];
  const built = (slot) => (slot.picks || []).map((id) => kept.find((p) => p.id === id)).filter(Boolean)
    .map((p) => `her ${kindOf(p.kind).label.toLowerCase()}${p.label ? ` "${p.label}"` : ""}`).join(" and ");
  const later = rows.filter((r) => r.slot.n > n).map((r) => ({ n: r.slot.n, topic: `${clip(r.slot.topic, 200)}${built(r.slot) ? ` — built on ${built(r.slot)}` : ""}` }));
  const self = rows.find((r) => r.slot.n === n)?.slot || null;
  return {
    name: clip(arc.name, 80),
    about: clip(arc.brief?.about, 600),
    goal: clip(arc.brief?.goal, 300),
    n,
    count: rows.length,
    topic: clip(self?.topic, 300),
    earlier,
    later,
  };
}

/* ── When an earlier post changes (her ask, 2026-09-30) ──────────────────────
   Post 3 was written FROM posts 1 and 2 as they were then (`arcBasis`, stamped
   by postRequest on both paths to Brill). If she rewrites or edits post 1
   afterwards, post 3 may no longer follow on — and nothing on screen said so.
   `arcDrift` names the earlier posts that differ from what this one was
   written from: changed, written for the first time since, or deleted.
   Never a rewrite (her rule: a flag, never a rewrite) — and hers to dismiss:
   `arcBasisNow` is what "still fine" stores, so the flag goes until an
   earlier post changes AGAIN.
   A post with no basis (written by hand, or before 2026-09-30) is never
   flagged: there is no record of what it followed. */
export function arcBasisNow(arc, posts = [], n) {
  return arcBasisOf(arcContext(arc, posts, n));
}

export function arcDrift(arc, posts = [], n) {
  const { rows } = arcRows(arc, posts);
  const row = rows.find((r) => r.slot.n === n);
  const basis = row?.written ? row.post.inputs?.arcBasis : null;
  if (!basis || typeof basis !== "object") return [];
  const now = arcBasisNow(arc, posts, n);
  return rows.filter((r) => r.slot.n < n && (basis[String(r.slot.n)] ?? null) !== (now[String(r.slot.n)] ?? null)).map((r) => r.slot.n);
}

/* The post an arc slot starts as. Nothing is written to the database until it
   has words (the hub's savePost), so an abandoned draft leaves no row. */
export function slotPost(arc, slot, id, library = []) {
  const pic = slotPicture(arc, slot, library);
  return {
    id,
    arcId: arc.id,
    title: "",
    platform: arc.platform || "linkedin",
    caption: "",
    firstComment: "",
    offerId: arc.offerId || null,
    scheduledDate: slot.date || null,
    inputs: {
      arcSlot: slot.n,
      pillar: slot.pillar || null,
      theme: slot.theme || null,
      hookCategory: slot.hook || null,
      cta: slot.ask && slot.ask.startsWith("link:") ? "library" : (slot.ask || "none"),
      length: slotLength(arc, slot) || null,
      context: slotAngle(slot, library) || null,
    },
    /* Its picture is there from the start, on BOTH paths to Brill — the arc's
       sparkle and the post screen — so her "what's in it" line rides with
       either draft, and she sees it before a word is written. */
    ...(pic ? { visual: withPicture(null, { url: pic.url, about: pic.about }) } : {}),
    ctaId: slot.ask && slot.ask.startsWith("link:") ? slot.ask.slice(5) : null,
    status: "draft",
  };
}

/* What a slot hands `postRequest` as her choice. */
export const slotChoice = (arc, slot) => ({
  pillar: slot.pillar || "",
  theme: slot.theme || "",
  hook: slot.hook || "",
  offerId: arc.offerId || null,
  ask: slot.ask || "none",
  length: slotLength(arc, slot),
});


/* AN ARC POST'S DAY, MOVED ON THE POST (2026-10-06). A post now has its own
   date field and a send that asks when, so an arc's post can move — and the
   arc's plan has to move with it, or the Arcs tab and the post disagree about
   when it goes out. Only that slot moves; the arc's span widens if the post
   moved outside it, and never shrinks under her. Returns the SAME arc object
   when nothing changes, so the caller can skip the write. */
export function withSlotDay(arc, n, day) {
  const slots = arc?.brief?.slots || [];
  const slot = slots.find((s) => s.n === n);
  if (!slot || !/^\d{4}-\d{2}-\d{2}$/.test(String(day || "")) || slot.date === day) return arc;
  const next = slots.map((s) => (s.n === n ? { ...s, date: day } : s));
  const start = [arc.startDate, day].filter(Boolean).sort()[0];
  const end = [arc.endDate, day].filter(Boolean).sort().at(-1);
  return { ...arc, startDate: start, endDate: end, brief: { ...arc.brief, slots: next } };
}
